import React, { useCallback, ChangeEvent, useState } from 'react';

import { isEmptyString, validEmail } from 'Shared/helpers';
import TextField from 'Shared/components/TextField/TextField';

// Hooks
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import useValidateEmployeeEmail from 'Shared/utils/hooks/use-validate-employee-email';

import './AddTeammateForm.scss';

const EmailLabel = ({ isEmailMandatory = false }: { isEmailMandatory: boolean }) => (
  <div className="text-field-label text-muted">
    Invite Via Email
    {!isEmailMandatory && <span className="ml-1">(Optional)</span>}
  </div>
);

type Props = {
  isDirectInvite?: boolean;
  isEmailMandatory?: boolean;
  emailPlaceholder?: string;
  formValues: {
    firstName: string;
    lastName: string;
    email: string;
    personalMessage: string;
  };
  onTextFieldsChange: (event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => void;
};

function AddTeammateForm({
  isDirectInvite = false,
  isEmailMandatory,
  emailPlaceholder = 'Enter Email',
  formValues,
  onTextFieldsChange,
}: Props) {
  const [errorMessage, setErrorMessage] = useState(null);
  const { organization } = useOrganization();
  const { isEmployeeEmailOrValidDomain } = useValidateEmployeeEmail();

  const getEmailFormatErrorMessage = email => {
    if (!email) return null;
    if (!validEmail(email)) return `Invalid email address: ${email}`;
    if (!isEmployeeEmailOrValidDomain({ email }))
      return `Invalid email domain, must be one of the following domains: ${organization.pi_allowed_domains.join(
        ', '
      )}`;
    return null;
  };

  const isEmptyTeammateEmail = useCallback(() => {
    const email = formValues.email;
    if (!email) return true;
    return isEmptyString(email);
  }, [formValues.email]);

  const handleBlurEmailInput = useCallback(() => setErrorMessage(getEmailFormatErrorMessage(formValues.email)), [
    formValues.email,
  ]);

  return (
    <div className="add-teammate-form">
      <div className="d-flex justify-content-between flex-column flex-sm-row align-items-end">
        <div className="w-100 mr-0 mr-sm-2">
          <TextField
            name="firstName"
            type="text"
            label="Name *"
            placeholder="First Name"
            value={formValues.firstName}
            onChange={onTextFieldsChange}
          />
        </div>
        <TextField
          name="lastName"
          type="text"
          placeholder="Last Name"
          value={formValues.lastName}
          onChange={onTextFieldsChange}
        />
      </div>

      {!isDirectInvite ? (
        <TextField
          name="email"
          type="text"
          label={<EmailLabel isEmailMandatory={isEmailMandatory} />}
          placeholder={emailPlaceholder}
          onBlur={handleBlurEmailInput}
          value={formValues.email}
          onChange={onTextFieldsChange}
        />
      ) : null}
      {errorMessage ? (
        <div className="alert alert-danger p-2 mt-2 mb-2 small w-100" role="alert">
          {errorMessage}
        </div>
      ) : null}
      {!isDirectInvite && !isEmailMandatory ? (
        <div className="add-teammate-form-field-comment text-muted mb-2">You can always invite them later</div>
      ) : null}

      {!isEmptyTeammateEmail() ? (
        <div className="d-flex flex-column mt-3">
          <div className="small font-weight-bold text-muted mb-1">Personal Message (optional)</div>
          <textarea
            name="personalMessage"
            className="w-100 border rounded p-2"
            value={formValues.personalMessage}
            onChange={onTextFieldsChange}
          />
        </div>
      ) : null}
    </div>
  );
}

export default AddTeammateForm;
